/**
 * Developers — public "Software Integrations" page (route stays /developers).
 *
 * Audience: software providers evaluating whether to build on Simkura.
 * Shows who already integrates Simkura hardware into their software
 * (Bluewave Security, OmoLock, Loudin), then the developer-platform pitch:
 * the app → API → device flow, the REST + webhooks integration surface, and
 * the authentication models.
 *
 * MQTT is no longer offered as a customer integration channel — REST/webhooks
 * is the surface. Nav + footer live in PublicLayout; this page only declares
 * its content.
 */

import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import {
  IconApi,
  IconKey,
  IconUserCheck,
  IconBolt,
  IconWebhook,
  IconDeviceDesktop,
  IconArrowRight,
  IconExternalLink,
} from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';

// Current software integrations — access-control platforms that run on
// Simkura hardware today. Loudin is the open source one; it links through to
// its own page (/open-source) for the full mission/repo story rather than
// duplicating it here.
const INTEGRATIONS: Array<{
  id: string;
  name: string;
  tagline: string;
  to: string;
  external?: boolean;
}> = [
  {
    id: 'bluewave',
    name: 'Bluewave Security',
    tagline: 'Multi-tenant access control and door-lock management, built on Simkura hardware.',
    to: 'https://bluewavesecurity.com',
    external: true,
  },
  {
    id: 'omolock',
    name: 'OmoLock',
    tagline: 'Cellular smart locks for business doors, managed from one dashboard.',
    to: 'https://omolock.com',
    external: true,
  },
  {
    id: 'loudin',
    name: 'Loudin',
    tagline: 'The open source access control app we sponsor — free to run on Simkura hardware.',
    to: '/open-source',
  },
];

// ── Layout primitives ─────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 clamp(20px, 6vw, 64px);
`;

const Section = styled.section`
  padding: clamp(40px, 7vh, 80px) 0;
`;

// ── Hero — washed text hero, matching Home. No hero card, no hero image.

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(56px, 11vh, 120px) 0 clamp(32px, 5vh, 56px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(60% 80% at 20% 0%, #d8e4f0 0%, transparent 60%),
      radial-gradient(50% 70% at 85% 10%, #dddcea 0%, transparent 55%),
      radial-gradient(40% 50% at 60% 100%, #c9d6df22 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => '#0d253d'};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
`;

const HeroTitle = styled.h1`
  font-size: clamp(36px, 6vw, 56px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(5)} 0;
  max-width: 16ch;
`;

const HeroText = styled.p`
  font-size: clamp(16px, 2vw, 19px);
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.55;
  max-width: 48ch;
  margin: 0;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(7)};
`;

const Chip = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.secondary};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
`;

// ── Section heading ─────────────────────────────────────────────────────────────

const SectionLabel = styled.p`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => '#0d253d'};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 3.4vw, 34px);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SectionLede = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.6;
  max-width: 660px;
  margin: 0 0 ${({ theme }) => theme.spacing(8)} 0;
`;

// ── Flow visual ─────────────────────────────────────────────────────────────────

const FlowRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(10)};
`;

const FlowNode = styled.span<{ accent?: boolean }>`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.font.weight.medium};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(5)};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  border: 1px solid
    ${({ theme, accent }) => (accent ? '#0d253d' : theme.border.color.medium)};
  background: ${({ theme, accent }) => (accent ? '#d8e4f0' : theme.background.primary)};
  color: ${({ theme, accent }) => (accent ? '#0d253d' : theme.font.color.primary)};
`;

const FlowArrow = styled.span`
  color: ${({ theme }) => theme.font.color.tertiary};
  display: inline-flex;
`;

// ── Cards ───────────────────────────────────────────────────────────────────────

const Grid = styled.div<{ min?: string }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${({ min }) => min ?? '280px'}, 1fr));
  gap: ${({ theme }) => theme.spacing(5)};
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(7)};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background: ${({ theme }) => theme.background.primary};
`;

const IconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => '#d8e4f0'};
  color: ${({ theme }) => '#0d253d'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Pill = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.font.weight.medium};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => '#0d253d'};
  background: ${({ theme }) => '#d8e4f0'};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  padding: 2px 8px;
`;

const CardText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.6;
  margin: 0;
`;

const BulletList = styled.ul`
  margin: ${({ theme }) => theme.spacing(4)} 0 0;
  padding-left: 1.1em;

  li {
    font-size: 14px;
    color: ${({ theme }) => theme.font.color.secondary};
    line-height: 1.6;
    margin: ${({ theme }) => theme.spacing(1)} 0;
  }
`;

// ── Closing CTA band ──────────────────────────────────────────────────────────

const CtaBand = styled.div`
  text-align: center;
  padding: clamp(44px, 7vh, 76px) 24px;
  border-radius: ${({ theme }) => theme.border.radius.xl};
  background: linear-gradient(135deg, #1b3a5e 0%, #0d253d 100%);
  color: #fff;

  h2 {
    font-size: clamp(26px, 4vw, 40px);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #fff;
    margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  }
  p {
    color: rgba(255, 255, 255, 0.88);
    font-size: 18px;
    line-height: 1.55;
    margin: 0 auto ${({ theme }) => theme.spacing(7)};
    max-width: 56ch;
  }
`;

const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  justify-content: center;
  align-items: center;
`;

const CtaWhiteExternal = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 52px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: #fff;
  color: ${({ theme }) => '#0d253d'};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

const CtaGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: 52px;
  padding: 0 22px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.12); }
`;

const CardLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => '#0d253d'};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

const CardRouteLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => '#0d253d'};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

// ── Page ────────────────────────────────────────────────────────────────────────

function Developers() {
  return (
    <PublicLayout>
      <HeroSection>
        <Container>
          <Hero>
            <Eyebrow>Software integrations</Eyebrow>
            <HeroTitle>Build software connected to Simkura</HeroTitle>
            <HeroText>
              Software providers can connect to all hardware provider through one API. 
              Reach out to build on Simkura today. 
            </HeroText>
            <ChipRow>
              <Chip>REST API</Chip>
              <Chip>Scoped API keys</Chip>
              <Chip>Signed webhooks</Chip>
            </ChipRow>
          </Hero>
        </Container>
      </HeroSection>

      {/* Who's building on Simkura */}
      <Section>
        <Container>
          <SectionLabel>Who's building on Simkura</SectionLabel>
          <SectionTitle>Current software integrations</SectionTitle>
          <SectionLede>
            These platforms already run their access control on Simkura
            hardware today.
          </SectionLede>
          <Grid min="280px">
            {INTEGRATIONS.map((i) => (
              <Card key={i.id}>
                <CardTitle>{i.name}</CardTitle>
                <CardText>{i.tagline}</CardText>
                {i.external ? (
                  <CardLink href={i.to} target="_blank" rel="noreferrer">
                    Visit {i.name} <IconExternalLink size={14} />
                  </CardLink>
                ) : (
                  <CardRouteLink to={i.to}>
                    Learn more <IconArrowRight size={14} />
                  </CardRouteLink>
                )}
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* How it works */}
      <Section>
        <Container>
          <SectionLabel>How it works</SectionLabel>
          <SectionTitle>Your app talks to the API; the API talks to the devices</SectionTitle>
          <SectionLede>
            Send a command and Simkura validates, persists, and forwards it down to the device —
            then routes device events back up to you in real time.
          </SectionLede>

          <FlowRow>
            <FlowNode>Your app</FlowNode>
            <FlowArrow><IconArrowRight size={18} /></FlowArrow>
            <FlowNode accent>Simkura API</FlowNode>
            <FlowArrow><IconArrowRight size={18} /></FlowArrow>
            <FlowNode>Hardware providers</FlowNode>
          </FlowRow>

          <Grid min="260px">
            <Card>
              <IconWrap><IconBolt size={20} /></IconWrap>
              <CardTitle>Send commands</CardTitle>
              <CardText>
                Lock, unlock, provision credentials and schedules, and run diagnostics. Commands are
                queued automatically for sleeping devices.
              </CardText>
            </Card>
            <Card>
              <IconWrap><IconWebhook size={20} /></IconWrap>
              <CardTitle>Receive events</CardTitle>
              <CardText>
                Access grants and denials, lock-state changes, wake/sleep, and command results —
                delivered to you in real time.
              </CardText>
            </Card>
            <Card>
              <IconWrap><IconDeviceDesktop size={20} /></IconWrap>
              <CardTitle>Read state</CardTitle>
              <CardText>
                Fetch a device's current lock, power, battery, and firmware on demand for dashboards
                or reconciliation.
              </CardText>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* Integration surface */}
      <Section>
        <Container>
          <SectionLabel>Integration</SectionLabel>
          <SectionTitle>Commands out, events in — all over HTTPS</SectionTitle>
          <SectionLede>
            One clean surface: send commands with HTTP requests, receive events on an endpoint you
            host. No broker connections or persistent sockets to manage.
          </SectionLede>
          <Grid min="320px">
            <Card>
              <IconWrap><IconApi size={20} /></IconWrap>
              <CardTitle>REST API <Pill>HTTPS</Pill></CardTitle>
              <CardText>
                Read device state and send commands with plain HTTP requests. The fastest path for
                any backend with an HTTP client.
              </CardText>
              <BulletList>
                <li>Auth: scoped API keys</li>
                <li>Async commands — queued for sleeping devices</li>
                <li>Scoped, revocable credentials</li>
              </BulletList>
            </Card>
            <Card>
              <IconWrap><IconWebhook size={20} /></IconWrap>
              <CardTitle>Webhooks <Pill>Signed</Pill></CardTitle>
              <CardText>
                Access decisions, lock-state changes, command results, and device health alarms —
                pushed to your endpoint as they happen.
              </CardText>
              <BulletList>
                <li>HMAC-signed deliveries you can verify</li>
                <li>Filter by event type, category, or device</li>
                <li>Automatic retries with delivery logs</li>
              </BulletList>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* Authentication */}
      <Section>
        <Container>
          <SectionLabel>Authentication</SectionLabel>
          <SectionTitle>Secure, scoped access</SectionTitle>
          <SectionLede>
            API keys are limited to least-privilege scopes — full setup and credential management
            live in the dashboard.
          </SectionLede>
          <Grid min="280px">
            <Card>
              <IconWrap><IconKey size={20} /></IconWrap>
              <CardTitle>API keys <Pill>server-to-server</Pill></CardTitle>
              <CardText>
                Long-lived, scoped, revocable keys for headless integrations. The simplest path when
                your own backend calls Simkura — issued with the scopes you need.
              </CardText>
            </Card>
            <Card>
              <IconWrap><IconUserCheck size={20} /></IconWrap>
              <CardTitle>User login</CardTitle>
              <CardText>
                Sign in with a Simkura account for full dashboard access to manage devices,
                credentials, and integrations.
              </CardText>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* CTA */}
      <Section>
        <Container>
          <CtaBand>
            <h2>Get API access</h2>
            <p>
              The API is open — no request or approval needed. The full
              reference lives at docs.simkura.com; create an account when
              you're ready for a live API key.
            </p>
            <CtaActions>
              <CtaWhiteExternal href="https://docs.simkura.com" target="_blank" rel="noreferrer">
                Read the docs
                <IconArrowRight size={18} />
              </CtaWhiteExternal>
              <CtaGhost to="/signup">Create an account</CtaGhost>
            </CtaActions>
          </CtaBand>
        </Container>
      </Section>
    </PublicLayout>
  );
}

export default Developers;
